/**
 * Term-and-description list, separated by hairlines rather than enclosed in
 * boxes. This is the same separation model the in-development portfolio rows
 * use, and it is what replaced the bordered-box pattern site-wide.
 *
 * Real <dl>/<dt>/<dd>: these are term-definition pairs, and a screen reader
 * should announce the term and its description as distinct things rather than
 * as one run-on string.
 *
 * `detail` accepts a string or an array of strings — an array renders multiple
 * <dd> elements under a single <dt>, which is what the per-app data disclosures
 * need (what is collected, and what it is used for).
 *
 * `meta` puts a short value hard right on the term line. It exists for prices,
 * where the number is the key fact rather than an ornament — the one place
 * besides status pills where the emerald accent earns its place in body content.
 */
export default function SpecList({ items, className = "" }) {
  return (
    <dl className={className}>
      {items.map(({ term, detail, badge, meta }) => {
        const details = Array.isArray(detail) ? detail : [detail];
        return (
          <div
            key={term}
            className="border-t border-line py-4 first:border-t-0 first:pt-0 last:pb-0"
          >
            <dt
              className={`text-sm font-medium text-white/90 ${
                meta ? "flex items-baseline justify-between gap-4" : ""
              }`}
            >
              <span>
                {term}
                {/* Neutral by design. Product colour is confined to the logo
                    tile, and the signal here is the word, not the hue. */}
                {badge ? (
                  <span className="ml-2 align-middle text-[10px] uppercase tracking-[0.14em] text-white/85">
                    {badge}
                  </span>
                ) : null}
              </span>
              {meta ? (
                <span className="shrink-0 whitespace-nowrap tabular-nums text-[#10B981]">
                  {meta}
                </span>
              ) : null}
            </dt>
            {details.map((d, i) => (
              <dd
                key={i}
                // Arbitrary opacity syntax is required: 62 is not on Tailwind's
                // default scale, so a bare text-white/62 generates nothing and
                // the description silently inherits the parent's colour.
                className={`text-sm leading-relaxed text-white/[0.62] ${
                  i === 0 ? "mt-1.5" : "mt-2"
                }`}
              >
                {d}
              </dd>
            ))}
          </div>
        );
      })}
    </dl>
  );
}
